import { memo, useCallback, useMemo } from 'react';
import Table, { TableEmptyRowsProps } from '@antimatterhq/ui/components/table';
import { TABLE_SELECT_COLUMN_ID } from '@antimatterhq/ui/constants/table';
import { TableColumn } from '@antimatterhq/ui/types/TableColumn';
import isEqual from 'react-fast-compare';

export interface CustomHeaderCellProps {
  column: TableColumn;
}

export interface CustomCellProps<T> {
  item: T;
  column: TableColumn;
}

export type CustomBodyCells<T extends { id: string }> = Record<
  string,
  React.ComponentType<CustomCellProps<T>> | React.ReactNode
>;

export type CustomHeaderCells = Record<
  string,
  React.ComponentType<CustomHeaderCellProps> | React.ReactNode
>;

export type TableRowsEmptyState = {
  EmptyTableComponent?: React.ComponentType<TableEmptyRowsProps>;
  emptyTableMessage?: string;
};

export type TableRowsSelection = {
  isSelected: (id: string) => boolean;
  selectedRows: string[];
  onToggleSelectAll: (isSelectedAll: boolean) => void;
  isAllSelected: boolean;
  onToggleSelect: (id: string, checked: boolean) => void;
};

type UseTableRowsProps<T extends { id: string }> = {
  items: T[];
  columns: TableColumn[];
  isLoading?: boolean;
  pageSize: number;
  customHeaderCells?: CustomHeaderCells;
  customBodyCells?: CustomBodyCells<T>;
  emptyState?: TableRowsEmptyState;
  selection?: TableRowsSelection;
  'data-testid'?: string;
  expandable?: boolean;
};

export const MemoizedCustomCell = memo(
  <T extends { id: string }>({
    component: Component,
    item,
    column,
  }: {
    component: React.ComponentType<CustomCellProps<T>> | React.ReactNode;
    item: T;
    column: TableColumn;
  }) => {
    if (typeof Component === 'function') {
      return <Component item={item} column={column} />;
    }

    return Component;
  },
) as <T extends { id: string }>(props: {
  component: React.ComponentType<CustomCellProps<T>> | React.ReactNode;
  item: T;
  column: TableColumn;
}) => JSX.Element;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
MemoizedCustomCell.displayName = 'MemoizedCustomCell';

export const MemoizedTableRow = memo<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any;
  expandable: boolean;
  dataTestId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderBodyCells: (item: any) => React.ReactNode;
}>(
  ({ item, expandable, dataTestId, renderBodyCells }) => {
    return (
      <Table.Row
        key={item.id}
        hover
        expandable={expandable}
        data-testid={`${dataTestId}-row-${item.id}`}
      >
        {renderBodyCells(item)}
      </Table.Row>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if the specific item data changed
    return (
      isEqual(prevProps.item, nextProps.item) &&
      isEqual(prevProps.renderBodyCells, nextProps.renderBodyCells) &&
      prevProps.expandable === nextProps.expandable &&
      prevProps.dataTestId === nextProps.dataTestId
    );
  },
);

MemoizedTableRow.displayName = 'MemoizedTableRow';

export function useTableRows<T extends { id: string }>(
  props: UseTableRowsProps<T>,
) {
  const {
    items,
    columns,
    isLoading,
    pageSize,
    selection,
    'data-testid': dataTestId,
    expandable = false,
  } = props;

  const emptyState = useMemo(() => props.emptyState ?? {}, [props.emptyState]);

  const customHeaderCells = useMemo(
    () => props.customHeaderCells,
    [props.customHeaderCells],
  );

  const customBodyCells = useMemo(
    () => props.customBodyCells,
    [props.customBodyCells],
  );

  const {
    isSelected,
    selectedRows,
    onToggleSelectAll,
    isAllSelected,
    onToggleSelect,
  } = selection ?? {};

  const EmptyTableComponent = useMemo(() => {
    return emptyState.EmptyTableComponent ?? Table.EmptyState;
  }, [emptyState.EmptyTableComponent]);

  const renderHeaderCell = useCallback(
    (column: TableColumn) => {
      if (
        column.id === TABLE_SELECT_COLUMN_ID &&
        (!onToggleSelectAll || isAllSelected === undefined)
      ) {
        console.warn(
          'TABLE_SELECT_COLUMN_ID is defined but Table.SelectHeaderCell is missing required props: onToggleSelectAll and isAllSelected',
        );
      }

      if (
        column.id === TABLE_SELECT_COLUMN_ID &&
        onToggleSelectAll &&
        isAllSelected !== undefined
      ) {
        return isLoading ? (
          <Table.Head key={column.id} column={column}>
            {''}
          </Table.Head>
        ) : (
          <Table.Head key={column.id} column={column}>
            <Table.SelectHeaderCell
              isAllSelected={isAllSelected}
              onToggleSelectAll={onToggleSelectAll}
            />
          </Table.Head>
        );
      } else if (typeof customHeaderCells?.[column.id] === 'function') {
        const CustomCell = customHeaderCells?.[
          column.id
        ] as React.ComponentType<CustomHeaderCellProps>;

        return (
          <Table.Head key={column.id} column={column}>
            <CustomCell column={column} />
          </Table.Head>
        );
      } else if (customHeaderCells?.[column.id] !== undefined) {
        return (
          <Table.Head key={column.id} column={column}>
            {customHeaderCells?.[column.id] as React.ReactNode}
          </Table.Head>
        );
      } else {
        return (
          <Table.Head key={column.id} column={column}>
            {column.title}
          </Table.Head>
        );
      }
    },
    [customHeaderCells, isAllSelected, isLoading, onToggleSelectAll],
  );

  const headerRow = useMemo(() => {
    return (
      <Table.Row>{columns.map((column) => renderHeaderCell(column))}</Table.Row>
    );
  }, [columns, renderHeaderCell]);

  const renderBodyCell = useCallback(
    (item: T, column: TableColumn, cellDataTestId?: string) => {
      if (
        column.id === TABLE_SELECT_COLUMN_ID &&
        (!isSelected || !onToggleSelect)
      ) {
        console.warn(
          'TABLE_SELECT_COLUMN_ID is defined but Table.SelectHeaderCell is missing required props: onToggleSelectAll and isAllSelected',
        );
      }

      if (
        column.id === TABLE_SELECT_COLUMN_ID &&
        isSelected &&
        onToggleSelect
      ) {
        return (
          <Table.Cell
            column={column}
            key={column.id}
            data-testid={`${cellDataTestId}-select-cell-${item.id}`}
          >
            <Table.SelectCell
              selected={isSelected(item.id)}
              id={item.id}
              onToggleSelect={onToggleSelect}
            />
          </Table.Cell>
        );
      } else if (typeof customBodyCells?.[column.id] === 'function') {
        // TODO: This is problematic because it will cause the cell to be re-rendered
        // when the item changes. We need to find a way to memoize the cell or remove this option.
        const CustomCell = customBodyCells?.[column.id] as React.ComponentType<
          CustomCellProps<T>
        >;

        return (
          <Table.Cell
            column={column}
            key={column.id}
            data-testid={`${cellDataTestId}-cell-${column.id}-${item.id}`}
          >
            <MemoizedCustomCell
              component={CustomCell}
              item={item}
              column={column}
            />
          </Table.Cell>
        );
      } else if (customBodyCells?.[column.id] !== undefined) {
        return (
          <Table.Cell
            column={column}
            key={column.id}
            data-testid={`${cellDataTestId}-cell-${column.id}-${item.id}`}
          >
            {customBodyCells?.[column.id] as React.ReactNode}
          </Table.Cell>
        );
      } else {
        return (
          <Table.Cell
            column={column}
            key={column.id}
            data-testid={`${cellDataTestId}-cell-${column.id}-${item.id}`}
          >
            <Table.SafeValueWrapper value={item[column.id as keyof T]} />
          </Table.Cell>
        );
      }
    },
    [customBodyCells, isSelected, onToggleSelect],
  );

  const renderBodyCells = useCallback(
    (item: T) => {
      return columns.map((column) => renderBodyCell(item, column, dataTestId));
    },
    [columns, dataTestId, renderBodyCell],
  );

  const bodyRows = useMemo(() => {
    if (isLoading) {
      return [
        <Table.SkeletonRows
          key="skeleton-rows"
          rows={pageSize}
          columns={columns}
        />,
      ] as JSX.Element[];
    }

    if (items.length === 0 && !isLoading) {
      return [
        <EmptyTableComponent
          key="empty-table-component"
          message={emptyState.emptyTableMessage}
          columns={columns}
        />,
      ] as JSX.Element[];
    }

    return items.map((item) => (
      <MemoizedTableRow
        key={item.id}
        expandable={expandable}
        dataTestId={dataTestId}
        renderBodyCells={renderBodyCells}
        item={item}
      />
    ));
  }, [
    isLoading,
    items,
    pageSize,
    columns,
    EmptyTableComponent,
    emptyState.emptyTableMessage,
    expandable,
    dataTestId,
    renderBodyCells,
  ]);

  return { headerRow, bodyRows, selectedRows };
}
